<div class="dock" [class.dock--expanded]="panelExpanded()">
  @if (odinService.isAudioStarting() && !userActivation.activated()) {
    <div class="dock__mic-overlay">
      <div class="dock__mic-dialog">
        <span>{{ 'The page was reloaded, click to confirm' | translate }}</span>
        <button class="dock__mic-confirm-btn">{{ 'Confirm' | translate }}</button>
      </div>
    </div>
  }

  <div class="dock__shell">
    <div class="dock__expansion">
      @if (panelExpanded()) {
        <div class="dock__expansion-header">
          <span class="dock__expansion-title">
            @if (content() === 'audio-video-settings') {
              {{ 'Audio & Video Settings' | translate }}
            } @else {
              {{ 'Notifications' | translate }}
            }
          </span>
          <button type="button" class="dock__expansion-close" (click)="closeExpansion()">
            <fa-duotone-icon [icon]="faXmark"></fa-duotone-icon>
          </button>
        </div>
        <div class="dock__expansion-body">
          @switch (content()) {
            @case ('audio-video-settings') {
              <andor-general-settings></andor-general-settings>
            }
            @case ('notifications') {
              <andor-notifications-area></andor-notifications-area>
            }
          }
        </div>
      }
    </div>

    <div class="dock__bar">
      <andor-split-toggle-button
        [active]="!!micActive() && permissions.microphoneAccess() === 'granted'"
        [disabled]="micDisabled()"
        [tooltip]="
          showMicPermissionTooltip()
            ? micPermissionTip
            : micBlocked()
              ? ('Your browser blocks microphone access. Click for instructions.' | translate)
              : micActive()
                ? ('Mute Microphone' | translate)
                : ('Unmute Microphone' | translate)
        "
        [tooltipTitle]="
          showMicPermissionTooltip()
            ? ('Talking not permitted' | translate)
            : micBlocked()
              ? ('Permission blocked' | translate)
              : null
        "
        [tooltipVariant]="showMicPermissionTooltip() || micBlocked() ? 'warning' : 'default'"
        [popoverTitle]="'Microphone'"
        [options]="audioInputDevices()"
        [selectedOptionId]="activeAudioInputDevice()?.id ?? null"
        (toggleClicked)="toggleMic()"
        (optionSelected)="onAudioInputSelected($event)">
        <andor-volume-icon [level]="volumeLevel()">
          <fa-duotone-icon
            [animation]="odinService.isAudioStarting() ? 'beat-fade' : undefined"
            [icon]="micActive() ? faMicrophone : faMicrophoneSlash"></fa-duotone-icon>
        </andor-volume-icon>
      </andor-split-toggle-button>

      <andor-square-icon-btn
        [variant]="speakersActive() ? 'default' : 'off'"
        [tooltip]="
          (speakersActive() ? 'Mute Speakers/Headphones' : 'Unmute Speakers/Headphones') | translate
        "
        (clicked)="toggleSpeakers()">
        <fa-duotone-icon
          [icon]="speakersActive() ? faHeadphones : faHeadphonesSlash"></fa-duotone-icon>
      </andor-square-icon-btn>

      <andor-split-toggle-button
        [active]="!!camStream() && permissions.cameraAccess() === 'granted'"
        [disabled]="camDisabled()"
        [tooltip]="
          showVideoPermissionTooltip()
            ? ('Your current role &quot;[role]&quot; is not permitted to stream video in this room.'
              | translate: { role: appService.roleName() })
            : camStream()
              ? ('Turn off Camera' | translate)
              : ('Turn on Camera' | translate)
        "
        [tooltipTitle]="showVideoPermissionTooltip() ? ('Video not permitted' | translate) : null"
        [tooltipVariant]="showVideoPermissionTooltip() ? 'warning' : 'default'"
        [popoverTitle]="'Camera'"
        [options]="videoDevices()"
        [selectedOptionId]="selectedVideoDevice()?.id ?? null"
        (toggleClicked)="toggleCam()"
        (optionSelected)="onVideoDeviceSelected($event)">
        <fa-duotone-icon [icon]="camStream() ? faVideo : faVideoSlash"></fa-duotone-icon>
      </andor-split-toggle-button>

      <!-- Phones have no screen capture in the app. -->
      @if (screenShareSupported) {
        <andor-screen-share-button></andor-screen-share-button>
      }

      <andor-square-icon-btn
        [variant]="hasRaisedHand() ? 'active' : 'default'"
        [tooltip]="(hasRaisedHand() ? 'Lower Hand' : 'Raise Hand') | translate"
        (clicked)="toggleRaiseHand()">
        <fa-duotone-icon [icon]="faHandWave"></fa-duotone-icon>
      </andor-square-icon-btn>

      <span class="dock__divider"></span>

      <span class="dock__bell-wrap">
        <andor-square-icon-btn
          class="dock__bell"
          [class.bell-ringing]="bellRinging()"
          [variant]="panelExpanded() && content() === 'notifications' ? 'active' : 'default'"
          [tooltip]="'Show Notifications' | translate"
          (clicked)="toggleNotifications()">
          <fa-duotone-icon [icon]="faBell"></fa-duotone-icon>
        </andor-square-icon-btn>
        @if (notificationService.notificationsUnseen(); as unseen) {
          <span
            class="dock__notification-badge"
            [attr.aria-label]="unseen + ' unseen notifications'">
            {{ unseen > 99 ? '99+' : unseen }}
          </span>
        }
      </span>

      <andor-square-icon-btn
        [variant]="panelExpanded() && content() === 'audio-video-settings' ? 'active' : 'default'"
        [tooltip]="'Open Settings' | translate"
        (clicked)="toggleSettings()">
        <fa-duotone-icon [icon]="faSliders"></fa-duotone-icon>
      </andor-square-icon-btn>
    </div>
  </div>
</div>

<!-- Shown while the role lacks `useMicrophone` and no talk grant is active.
     The inert square-icon-btn is a purely visual replica of the raise-hand
     button so the user recognizes which control to press. -->
<ng-template #micPermissionTip>
  <div class="dock__mic-tip">
    <span>
      {{
        'Your current role "[role]" is not permitted to talk in this room.'
          | translate: { role: appService.roleName() }
      }}
    </span>
    <div class="dock__mic-tip-hint">
      <andor-square-icon-btn inert variant="default">
        <fa-duotone-icon [icon]="faHandWave"></fa-duotone-icon>
      </andor-square-icon-btn>
      <span>{{ 'Click the raise-hand button to request permission to talk.' | translate }}</span>
    </div>
  </div>
</ng-template>
